import type { Getter, Setter } from 'jotai';
import type { UndoRedoActionCommand } from '../../../../../modernization-common/undo-redo/types';
import { ANNOTATION_ML_STATE } from '../../../../labelling/constants';
import type { AnnotationT } from '../../../../labelling/types';
import type { FrameBoundary } from '../../../interpolation-manager/types';
import { BoundaryType } from '../../../interpolation-manager/types';
import type { FramePayload } from '../../../studio-store/types';
import { findInterpolationBoundaries } from '../../../utils/find-interpolation-boundaries';
import { reconstructAnnotationFromFramePayload } from '../../../utils/reconstruct-annotation-from-frame-payload';
import { annotationIdToLabelMapAtom, annotationMetadataAtom } from '../../annotation-atoms';
import { interpolationManagerAtom } from '../../interpolation';
import { studioStoreAtom } from '../../studio-store';
import { STUDIO_WRITE_ACTIONS } from '../../studio-store-write/actions';
import { dispatchStudioStoreWriteAtom } from '../../studio-store-write/dispatch-studio-store-write';
import type { UpdateAnnotationEventPayload } from '../events';
export const updateAnnotationCommandFactory = (get: Getter, set: Setter): UndoRedoActionCommand<UpdateAnnotationEventPayload, void> => {
  let previousFramePayload: FramePayload | null = null;
  let leftBoundary: FrameBoundary | null = null;
  let rightBoundary: FrameBoundary | null = null;
  return {
    execute: async payload => {
      const {
        frameIndex,
        annotationIdx,
        annotationData
      } = payload;

      // 1. Apply ML state pre-processing: AUTOMATED → ASSISTED
      let annotationToStore: AnnotationT = annotationData;
      if (annotationData.metadata?.mlData?.state === ANNOTATION_ML_STATE.AUTOMATED) {
        annotationToStore = {
          ...annotationData,
          metadata: {
            ...annotationData.metadata,
            mlData: {
              ...annotationData.metadata.mlData,
              state: ANNOTATION_ML_STATE.ASSISTED
            }
          }
        };
      }

      // 2. Capture previous frame payload for undo
      previousFramePayload = await getFramePayload(get, annotationIdx, frameIndex);

      // 3. Write annotation at frameIndex
      set(dispatchStudioStoreWriteAtom, {
        type: STUDIO_WRITE_ACTIONS.WRITE_ANNOTATION,
        frameIndex,
        annotationId: annotationIdx,
        annotation: annotationToStore
      });

      // 4. Find interpolation boundaries from updated metadata
      const metadata = get(annotationMetadataAtom).get(annotationIdx);
      if (!metadata) {
        return;
      }
      const boundaries = findInterpolationBoundaries(frameIndex, metadata);
      const {
        left,
        right
      } = boundaries;
      if (left === null && right === null) {
        return;
      }
      const interpolationManager = get(interpolationManagerAtom);
      if (!interpolationManager) {
        return;
      }

      // 5. Fetch boundary geometries in parallel — only for boundaries that exist.
      const [leftPayload, rightPayload] = await Promise.all([left !== null ? getFramePayload(get, annotationIdx, left.frameIndex) : Promise.resolve(null), right !== null ? getFramePayload(get, annotationIdx, right.frameIndex) : Promise.resolve(null)]);

      // 6. Store boundaries in closure for undo/redo — only when payload is available.
      // leftBoundary is always treated as KeyFrame regardless of findInterpolationBoundaries result.
      if (left !== null) {
        if (!leftPayload) {
          return;
        }
        leftBoundary = {
          index: left.frameIndex,
          type: BoundaryType.KeyFrame,
          geometry: leftPayload.geometry
        };
      }
      if (right !== null) {
        if (!rightPayload) {
          return;
        }
        rightBoundary = {
          index: right.frameIndex,
          type: right.type,
          geometry: rightPayload.geometry
        };
      }
      const currentFrameBoundary: FrameBoundary = {
        index: frameIndex,
        type: BoundaryType.KeyFrame,
        geometry: annotationToStore.data
      };

      // 7. Enqueue each side independently — caller orchestrates the two passes.
      if (leftBoundary !== null) {
        await interpolationManager.enqueue({
          annotationId: annotationIdx,
          startFrame: leftBoundary,
          endFrame: currentFrameBoundary
        });
      }
      if (rightBoundary !== null) {
        await interpolationManager.enqueue({
          annotationId: annotationIdx,
          startFrame: currentFrameBoundary,
          endFrame: rightBoundary
        });
      }
    },
    undo: async payload => {
      if (previousFramePayload === null) {
        return;
      }
      const {
        frameIndex,
        annotationIdx
      } = payload;
      const metadata = get(annotationMetadataAtom).get(annotationIdx);
      const annotationIdToLabelMap = get(annotationIdToLabelMapAtom);
      if (!metadata) {
        return;
      }

      // 1. Restore previous frame payload at frameIndex
      set(dispatchStudioStoreWriteAtom, {
        type: STUDIO_WRITE_ACTIONS.WRITE_ANNOTATION,
        frameIndex,
        annotationId: annotationIdx,
        annotation: reconstructAnnotationFromFramePayload(previousFramePayload, metadata, annotationIdToLabelMap)
      });
      if (leftBoundary === null && rightBoundary === null) {
        return;
      }
      const interpolationManager = get(interpolationManagerAtom);
      if (!interpolationManager) {
        return;
      }

      // 2. Re-interpolate surrounding frames.
      //    Interpolate from leftBoundary to previousFrameBoundary and from
      //    previousFrameBoundary to rightBoundary. When interpolating always
      //    mark previousFrameBoundary as a KeyFrame, so that previous
      //    interpolation stays intact from left boundary and right boundary.
      const previousFrameBoundary: FrameBoundary = {
        index: frameIndex,
        type: BoundaryType.KeyFrame,
        geometry: previousFramePayload.geometry
      };
      if (leftBoundary !== null) {
        await interpolationManager.enqueue({
          annotationId: annotationIdx,
          startFrame: leftBoundary,
          endFrame: previousFrameBoundary
        });
      }
      if (rightBoundary !== null) {
        await interpolationManager.enqueue({
          annotationId: annotationIdx,
          startFrame: {
            ...previousFrameBoundary,
            type: BoundaryType.KeyFrame
          },
          endFrame: rightBoundary
        });
      }
    }
  };
};
const getFramePayload = async (get: Getter, annotationId: string, frameIndex: number): Promise<FramePayload | null> => {
  const studioStore = get(studioStoreAtom);
  if (!studioStore) {
    return null;
  }
  return studioStore.getAnnotationFrameAtIndex(frameIndex, annotationId);
};